import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../config';
import { auth } from '../lib/firebase';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Process session_id from URL hash (for Google OAuth callback)
  useEffect(() => {
    const processSessionId = async () => {
      const hash = window.location.hash;
      if (hash.includes('session_id=')) {
        const sessionId = hash.split('session_id=')[1].split('&')[0];
        
        try {
          const response = await axios.post(`${API}/auth/session`, {
            session_id: sessionId,
          }, {
            withCredentials: true,
          });
          
          setUser(response.data);
          
          // Clean up URL
          window.history.replaceState({}, document.title, window.location.pathname);
        } catch (error) {
          console.error('Session processing error:', error);
        }
      }
      
      // Check existing session
      try {
        const response = await axios.get(`${API}/auth/me`, {
          withCredentials: true,
        });
        setUser(response.data);
      } catch (error) {
        // Not authenticated
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    processSessionId();
  }, []);

  const logout = async () => {
    try {
      if (auth) {
        await auth.signOut();
      }
      await axios.post(`${API}/auth/logout`, {}, {
        withCredentials: true,
      });
      setUser(null);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Exchanging Firebase ID token for a backend session
  const loginWithFirebaseToken = async (idToken) => {
    try {
      const response = await axios.post(`${API}/auth/firebase`, {
        id_token: idToken,
      }, {
        withCredentials: true,
      });
      setUser(response.data);
      return response.data; // includes is_new_user flag
    } catch (error) {
      console.error('Firebase session exchange error:', error);
      throw error;
    }
  };

  // Function to manually set user (used by phone/email login)
  const loginWithCredentials = (userData) => {
    setUser(userData);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      logout, 
      setUser: loginWithCredentials,
      loginWithFirebaseToken
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
